import { type ReactNode } from "react";

import { NotFound } from "@/pages";

import { ANDROID_PLATFORM_OS, IOS_PLATFORM_OS, type MobileApplicationPlatform } from "./constants";
import useDownloadMobileApplicationsConfig from "./useDownloadMobileApplicationsConfig";

type MobileApplicationsRouteGuardProps = {
  children: ReactNode;
  requiredType?: MobileApplicationPlatform;
};

function hasRequiredMobileApplication({
  androidApplication,
  hasMobileApplications,
  iosApplication,
  requiredType,
}: {
  androidApplication: ReturnType<typeof useDownloadMobileApplicationsConfig>["androidApplication"];
  hasMobileApplications: boolean;
  iosApplication: ReturnType<typeof useDownloadMobileApplicationsConfig>["iosApplication"];
  requiredType?: MobileApplicationPlatform;
}): boolean {
  if (requiredType === ANDROID_PLATFORM_OS) {
    return Boolean(androidApplication);
  }

  if (requiredType === IOS_PLATFORM_OS) {
    return Boolean(iosApplication);
  }

  return hasMobileApplications;
}

export default function MobileApplicationsRouteGuard({ children, requiredType }: MobileApplicationsRouteGuardProps) {
  const { androidApplication, hasMobileApplications, iosApplication, isMobileApplicationsReady } = useDownloadMobileApplicationsConfig();

  if (!isMobileApplicationsReady) {
    return <div data-testid="MobileApplicationsRouteGuard-Loading" />;
  }

  if (
    !hasRequiredMobileApplication({
      androidApplication,
      hasMobileApplications,
      iosApplication,
      requiredType,
    })
  ) {
    return <NotFound />;
  }

  return <div data-testid="MobileApplicationsRouteGuard">{children}</div>;
}
